import { apiClient } from './apiClient';
import { getCachedData, setCachedData } from '../db/analyticsDb';
import * as mockData from './analyticsMockData';

const isMockEnabled = () => {
    return process.env.REACT_APP_ENABLE_MOCK_ANALYTICS === 'true';
};

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Voice Agent Template service for API operations
 */
export class AnalyticsService {
    constructor() {
        this.BASE_URL = '/analytics';
    }

    /**
     * Generic wrapper for caching API responses
     * @param {string} endpoint - The API endpoint
     * @param {Object} params - Query parameters
     * @param {Function} fetchFn - The actual API call function
     * @param {boolean} forceRefresh - If true, bypass cache
     */
    async withCache(endpoint, params, fetchFn, forceRefresh = false) {
        const cacheKey = `${endpoint}?${new URLSearchParams(params).toString()}`;
        
        if (!forceRefresh) {
            const cached = await getCachedData(cacheKey);
            if (cached) return cached;
        }

        const data = await fetchFn();
        await setCachedData(cacheKey, data);
        return data;
    }

    async getScoreREST() {
        return apiClient.get(`${this.BASE_URL}/score`);
    }

    async getAnalyticsREST(filters) {
        return apiClient.post(`${this.BASE_URL}/data`, filters);
    }

    async getBillingPerformance(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockBillingPerformance(filters);
        }

        const params = {};
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all' && value !== '') params[key] = value;
            });
        }
        
        const forceRefresh = !!filters?.forceRefresh;
        delete params.forceRefresh;

        return this.withCache(
            '/billing-performance', 
            params, 
            () => apiClient.get(`${this.BASE_URL}/billing-performance?${new URLSearchParams(params).toString()}`),
            forceRefresh
        );
    }

    async getOperationsEfficiency(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockOperationsEfficiency(filters);
        }

        const params = {};
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params[key] = value;
            });
        }

        const forceRefresh = !!filters?.forceRefresh;
        delete params.forceRefresh;

        return this.withCache(
            '/operations-efficiency', 
            params, 
            () => apiClient.get(`${this.BASE_URL}/operations-efficiency?${new URLSearchParams(params).toString()}`),
            forceRefresh
        );
    }

    async getWorkloadIntensity(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockWorkloadIntensity(filters);
        }

        const params = {};
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params[key] = value;
            });
        }
        const forceRefresh = !!filters?.forceRefresh;
        delete params.forceRefresh;
        return this.withCache(
            '/workload-intensity', 
            params, 
            () => apiClient.get(`${this.BASE_URL}/workload-intensity?${new URLSearchParams(params).toString()}`),
            forceRefresh
        );
    }

    async getScriptVolumeMix(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockScriptVolumeMix(filters);
        }

        const params = {};
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params[key] = value;
            });
        }
        const forceRefresh = !!filters?.forceRefresh;
        delete params.forceRefresh;
        return this.withCache(
            '/script-volume-mix', 
            params, 
            () => apiClient.get(`${this.BASE_URL}/script-volume-mix?${new URLSearchParams(params).toString()}`),
            forceRefresh
        );
    }

    async getClaimsDashboard(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockClaimsDashboard(filters);
        }

        const params = {};
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params[key] = value;
            });
        }
        const forceRefresh = !!filters?.forceRefresh;
        delete params.forceRefresh;
        return this.withCache(
            '/claims-dashboard', 
            params, 
            () => apiClient.get(`${this.BASE_URL}/claims-dashboard?${new URLSearchParams(params).toString()}`),
            forceRefresh
        );
    }

    async getInsuranceDashboard(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockInsuranceDashboard(filters);
        }

        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params.append(key, value);
            });
        }
        return apiClient.get(`${this.BASE_URL}/insurance-dashboard?${params.toString()}`);
    }

    async getProviderPerformance(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockProviderPerformance(filters);
        }

        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params.append(key, value);
            });
        }
        return apiClient.get(`${this.BASE_URL}/provider-performance?${params.toString()}`);
    }

    async getReferralProfitability(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockReferralProfitability(filters);
        }

        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params.append(key, value);
            });
        }
        return apiClient.get(`${this.BASE_URL}/referral-profitability?${params.toString()}`);
    }

    async getComplianceAnalytics() {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockComplianceAnalytics();
        }

        return apiClient.get(`${this.BASE_URL}/compliance`);
    }

    async getOrderAnalytics(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockOrderAnalytics(filters);
        }

        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params.append(key, value);
            });
        }
        return apiClient.get(`${this.BASE_URL}/order-analytics?${params.toString()}`);
    }

    async getSupplyChainMetrics(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockSupplyChainMetrics(filters);
        }

        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params.append(key, value);
            });
        }
        return apiClient.get(`${this.BASE_URL}/supply-chain?${params.toString()}`);
    }

    async getClaimsByInsurance(insuranceId) {
        if (isMockEnabled()) {
            await delay(300);
            return mockData.getMockClaimsByInsuranceAndPatient(insuranceId, 'all');
        }

        return apiClient.get(`${this.BASE_URL}/claims/insurance/${insuranceId}`);
    }

    async getInsurancePerformance(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockInsurancePerformance(filters);
        }

        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') params.append(key, value);
            });
        }
        return apiClient.get(`${this.BASE_URL}/insurance-performance?${params.toString()}`);
    }

    async getPipExhaustionReport(filters) {
        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all') {
                    if (Array.isArray(value)) {
                        params.append(key, value.join(','));
                    } else {
                        params.append(key, value);
                    }
                }
            });
        }
        return apiClient.get(`${this.BASE_URL}/pip-exhaustion-report?${params.toString()}`);
    }

    async getGeographicDistribution() {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockGeographicDistribution();
        }

        return apiClient.get(`${this.BASE_URL}/geographic-distribution`);
    }

    /**
     * Get report preview data for table display and filtering
     * @param {Object} filters - Filter options
     */
    async getReportPreview(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockReportPreview(filters);
        }

        const params = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '' && value !== 'all') {
                    if (Array.isArray(value)) {
                        value.forEach(v => params.append(key, v));
                    } else {
                        params.append(key, String(value));
                    }
                }
            });
        }
        return apiClient.get(`${this.BASE_URL}/report/preview?${params.toString()}`);
    }

    /**
     * Get report data for PDF generation
     * @param {string[]} medicationIds - Array of medication IDs to include in report
     * @param {Object} filterMetadata - Applied filters metadata for PDF header
     */
    async getReportData(medicationIds, filterMetadata) {
        return apiClient.post(`${this.BASE_URL}/report/data`, { medicationIds, filterMetadata });
    }

    /**
     * Get medication profile analytics for a patient
     * @param {string} patientId - ID of the patient
     */
    async getPatientMedicationProfile(patientId) {
        if (isMockEnabled()) {
            await delay(300);
            return mockData.getMockPatientMedicationProfile(patientId);
        }

        return apiClient.get(`${this.BASE_URL}/patient/${patientId}/medication-profile`);
    }

    async getPatientsByInsurance(insuranceId) {
        if (isMockEnabled()) {
            await delay(300);
            return mockData.getMockPatientsByInsurance(insuranceId);
        }

        return apiClient.get(`${this.BASE_URL}/insurance/${insuranceId}/patients`);
    }

    async getClaimsByInsuranceAndPatient(insuranceId, patientId) {
        if (isMockEnabled()) {
            await delay(300);
            return mockData.getMockClaimsByInsuranceAndPatient(insuranceId, patientId);
        }

        return apiClient.get(`${this.BASE_URL}/insurance/${insuranceId}/patient/${patientId}/claims`);
    }

    async getPatientAccountBalance(patientId, filters = {}) {
        if (isMockEnabled()) {
            await delay(300);
            return mockData.getMockPatientAccountBalance(patientId);
        }

        const params = {};
        if (patientId) params.patientId = patientId;
        if (filters?.startDate) params.startDate = filters.startDate;
        if (filters?.endDate) params.endDate = filters.endDate;
        if (filters?.dateRange) params.dateRange = filters.dateRange;
        if (filters?.claimId) params.claimId = filters.claimId;
        if (filters?.insuranceClaimId) params.insuranceClaimId = filters.insuranceClaimId;
        if (filters?.dateOfService) params.dateOfService = filters.dateOfService;
        if (filters?.dateOfInjury) params.dateOfInjury = filters.dateOfInjury;
        
        return apiClient.get(`${this.BASE_URL}/patient-account-balance?${new URLSearchParams(params).toString()}`);
    }

    /**
     * Preload all major analytics views to warm the cache.
     * This is useful for first-time visits or manual "force refresh" triggers.
     */
    async bulkSyncAll(filters = {}) {
        const views = [
            'getBillingPerformance',
            'getOperationsEfficiency',
            'getWorkloadIntensity',
            'getScriptVolumeMix',
            'getClaimsDashboard',
            'getProviderPerformance',
            'getComplianceAnalytics'
        ];

        const syncFilters = { ...filters, forceRefresh: true };
        
        // Execute all syncs in parallel to reduce total wait time
        const results = await Promise.allSettled(
            views.map(view => {
                if (view === 'getComplianceAnalytics') return this.getComplianceAnalytics();
                return this[view](syncFilters);
            })
        );

        return results;
    }

    async getMedicationStats(filters = {}) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockMedicationStats(filters);
        }

        const params = new URLSearchParams();
        if (filters.paymentDateStart) params.append('paymentDateStart', filters.paymentDateStart);
        if (filters.paymentDateEnd) params.append('paymentDateEnd', filters.paymentDateEnd);
        if (filters.dateOfServiceStart) params.append('dateOfServiceStart', filters.dateOfServiceStart);
        if (filters.dateOfServiceEnd) params.append('dateOfServiceEnd', filters.dateOfServiceEnd);
        if (filters.caseTypes && filters.caseTypes.length > 0) {
            filters.caseTypes.forEach(ct => params.append('caseTypes', ct));
        }
        if (filters.insuranceNames && filters.insuranceNames.length > 0) {
            filters.insuranceNames.forEach(name => params.append('insuranceNames', name));
        }
        if (filters.medicationStatuses && filters.medicationStatuses.length > 0) {
            filters.medicationStatuses.forEach(s => params.append('medicationStatuses', s));
        }
        return apiClient.get(`${this.BASE_URL}/medication-stats?${params.toString()}`);
    }

    async getClaimsBillingPerformance(filters) {
        if (isMockEnabled()) {
            await delay(500);
            return mockData.getMockClaimsBillingPerformance(filters);
        }

        const params = {};
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value !== 'all' && value !== '') params[key] = value;
            });
        }
        
        const forceRefresh = !!filters?.forceRefresh;
        delete params.forceRefresh;

        return this.withCache(
            '/claims-billing-performance', 
            params, 
            () => apiClient.get(`${this.BASE_URL}/claims-billing-performance?${new URLSearchParams(params).toString()}`),
            forceRefresh
        );
    }
}

// Create a singleton instance
export const analyticsService = new AnalyticsService();
